/**
 * SafetyAssessmentList Component
 * 
 * Displays a list of safety assessments with status indicators.
 */

import React from 'react';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  FileCheck,
  ChevronRight,
} from 'lucide-react';
import { EmptyState } from '@/components/ui';
import type { SafetyAssessment, SSAStage, SSAStatus } from '@aerotage/shared';

interface SafetyAssessmentListProps {
  assessments: SafetyAssessment[];
  onSelect: (assessment: SafetyAssessment) => void;
}

export const SafetyAssessmentList: React.FC<SafetyAssessmentListProps> = ({
  assessments,
  onSelect,
}) => {
  if (!assessments?.length) {
    return (
      <EmptyState
        title="No assessments found"
        description="Safety assessments will appear here once created."
        compact
      />
    );
  }

  return (
    <div className="space-y-4">
      {assessments.map((assessment) => (
        <SafetyAssessmentCard
          key={assessment.assessmentId}
          assessment={assessment}
          onClick={() => onSelect(assessment)}
        />
      ))}
    </div>
  );
};

interface SafetyAssessmentCardProps {
  assessment: SafetyAssessment;
  onClick: () => void;
}

const SafetyAssessmentCard: React.FC<SafetyAssessmentCardProps> = ({
  assessment,
  onClick,
}) => {
  const {
    systemName,
    stage,
    status,
    workProducts,
    traceabilitySummary,
    createdAt,
    updatedAt,
  } = assessment;

  // Calculate progress
  const progress = calculateProgress(workProducts);
  
  return (
    <div
      className="bg-card border border-border rounded-lg p-4 hover:border-primary transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-medium text-foreground">{systemName}</h3>
            <StageBadge stage={stage} />
            <StatusBadge status={status} />
          </div>

          {/* Dates */}
          <div className="text-sm text-muted-foreground mb-3">
            Created: {formatDate(createdAt)} | Updated: {formatDate(updatedAt)}
          </div>

          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Progress</span>
              <span className="text-foreground font-medium">{progress}%</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className={`h-full bg-primary transition-all duration-300 ${
                  progress >= 100 ? 'w-full' :
                  progress >= 90 ? 'w-11/12' :
                  progress >= 80 ? 'w-4/5' :
                  progress >= 75 ? 'w-3/4' :
                  progress >= 66 ? 'w-2/3' :
                  progress >= 50 ? 'w-1/2' :
                  progress >= 33 ? 'w-1/3' :
                  progress >= 25 ? 'w-1/4' :
                  progress >= 20 ? 'w-1/5' :
                  progress >= 10 ? 'w-1/12' : 'w-0'
                }`}
              />
            </div>
          </div>

          {/* Work Products Status */}
          <div className="flex flex-wrap gap-2">
            <WorkProductStatus
              name="SFHA"
              status={getWorkProductStatus(workProducts.sfha)}
            />
            <WorkProductStatus
              name="FTA"
              status={getFTAStatus(workProducts.fta)}
              count={workProducts.fta?.length}
            />
            <WorkProductStatus
              name="FMEA"
              status={getWorkProductStatus(workProducts.fmea)}
            />
            <WorkProductStatus
              name="CMA"
              status={getWorkProductStatus(workProducts.cma)}
            />
          </div>

          {/* Traceability summary if available */}
          {traceabilitySummary && traceabilitySummary.unlinkedEventsCount > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-warning">
              <AlertCircle className="w-4 h-4" />
              {traceabilitySummary.unlinkedEventsCount} events need failure rate data
            </div>
          )}
        </div>

        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
    </div>
  );
};

// Stage badge component
const StageBadge: React.FC<{ stage: SSAStage }> = ({ stage }) => {
  const isSSA = stage === 'ssa';
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded ${
        isSSA
          ? 'bg-primary/20 text-primary'
          : 'bg-info/20 text-info'
      }`}
    >
      {isSSA ? 'SSA' : 'PSSA'}
    </span>
  );
};

// Status badge component
const StatusBadge: React.FC<{ status: SSAStatus }> = ({ status }) => {
  const config: Record<SSAStatus, { color: string; icon: React.ReactNode; label: string }> = {
    draft: {
      color: 'bg-muted/20 text-muted-foreground',
      icon: <Clock className="w-3 h-3" />,
      label: 'Draft',
    },
    in_review: {
      color: 'bg-warning/20 text-warning',
      icon: <FileCheck className="w-3 h-3" />,
      label: 'In Review',
    },
    approved: {
      color: 'bg-success/20 text-success',
      icon: <CheckCircle className="w-3 h-3" />,
      label: 'Approved',
    },
    superseded: {
      color: 'bg-muted/20 text-muted-foreground',
      icon: <AlertCircle className="w-3 h-3" />,
      label: 'Superseded',
    },
  };

  const { color, icon, label } = config[status];

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded flex items-center gap-1 ${color}`}>
      {icon}
      {label}
    </span>
  );
};

// Work product status indicator
interface WorkProductStatusProps {
  name: string;
  status: 'not_started' | 'incomplete' | 'complete' | 'verified';
  count?: number;
}

const WorkProductStatus: React.FC<WorkProductStatusProps> = ({ name, status, count }) => {
  const statusConfig = {
    not_started: { icon: '○', color: 'text-muted-foreground' },
    incomplete: { icon: '◐', color: 'text-warning' },
    complete: { icon: '●', color: 'text-success' },
    verified: { icon: '✓', color: 'text-primary' },
  };

  const { icon, color } = statusConfig[status];

  return (
    <span className={`text-sm ${color} flex items-center gap-1`}>
      <span>{icon}</span>
      {name}
      {count !== undefined && count > 1 && <span>({count})</span>}
    </span>
  );
};

// Helper functions
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function calculateProgress(workProducts: SafetyAssessment['workProducts']): number {
  let score = 0;
  const weights = { sfha: 25, fta: 35, fmea: 25, cma: 15 };

  if (workProducts.sfha) {
    if (workProducts.sfha.status === 'complete' || workProducts.sfha.status === 'verified') {
      score += weights.sfha;
    } else {
      score += weights.sfha * 0.5;
    }
  }

  if (workProducts.fta && workProducts.fta.length > 0) {
    const completeFTAs = workProducts.fta.filter(
      (f) => f.status === 'complete' || f.status === 'verified'
    ).length;
    score += (completeFTAs / workProducts.fta.length) * weights.fta;
  }

  if (workProducts.fmea) {
    if (workProducts.fmea.status === 'complete' || workProducts.fmea.status === 'verified') {
      score += weights.fmea;
    } else {
      score += weights.fmea * 0.5;
    }
  }

  if (workProducts.cma) {
    if (workProducts.cma.status === 'complete' || workProducts.cma.status === 'verified') {
      score += weights.cma;
    } else {
      score += weights.cma * 0.5;
    }
  }

  return Math.round(score);
}

function getWorkProductStatus(
  wp: { status: string } | undefined
): 'not_started' | 'incomplete' | 'complete' | 'verified' {
  if (!wp) return 'not_started';
  return wp.status as 'incomplete' | 'complete' | 'verified';
}

function getFTAStatus(
  ftas: Array<{ status: string }> | undefined
): 'not_started' | 'incomplete' | 'complete' | 'verified' {
  if (!ftas || ftas.length === 0) return 'not_started';
  
  const allComplete = ftas.every(
    (f) => f.status === 'complete' || f.status === 'verified'
  );
  if (allComplete) return 'complete';
  
  return 'incomplete';
}
